import { type OnboardingType } from '#app/__generated__/api.js';
import { type useUserTypeSelectorSelectUserTypeMutation } from '#app/__generated__/relay/useUserTypeSelectorSelectUserTypeMutation.graphql.js';
import { useModal } from '#app/components/Modals/V2/GlobalModalSystem/useModal.js';
import { useToast } from '#app/components/Toasts/hooks/useToast.js';
import { useContraMutation } from '#app/hooks/useContraMutation.js';
import { useLocation } from '#app/hooks/useLocation.js';
import { useNavigation } from '#app/hooks/useNavigation.js';
import { useSelectedUserType } from '#app/hooks/useSelectedUserType.js';
import { useShouldUserOnboard } from '#app/hooks/useShouldUserOnboard.js';
import { logger } from '#app/services/logger.js';
import { graphql } from '@contra/relay';
import { getRoute } from '@contra/routes';
import { captureException } from '@contra/sentry/react';
import { useCallback } from 'react';

const log = logger.child({
  namespace: 'useUserTypeSelector',
});

type IndyClientSplitRoute =
  | '/client/home'
  | '/client/invoices'
  | '/client/messages'
  | '/client/opportunities'
  | '/client/projects'
  | '/client/wallet'
  | '/independent/home'
  | '/independent/invoices/new'
  | '/independent/messages'
  | '/independent/projects'
  | '/independent/wallet';

type useUserTypeSelector = ReturnType<typeof useSelectedUserType> & {
  toggleSelectedUserType: (args: {
    disableRoutingChecks?: boolean;
    skipToast?: boolean;
  }) => void;
};

const isIndyClientSplitRoute = (currentLocation: string): boolean => {
  const indyClientSplitRoutes: IndyClientSplitRoute[] = [
    '/client/messages',
    '/client/opportunities',
    '/client/projects',
    '/client/wallet',
    '/client/home',
    '/client/invoices',
    '/independent/messages',
    '/independent/projects',
    '/independent/wallet',
    '/independent/home',
    '/independent/invoices/new',
  ];

  return Boolean(
    currentLocation &&
      indyClientSplitRoutes.includes(currentLocation as IndyClientSplitRoute),
  );
};

// Gets target route when user is on an IndyClientSplitRoute
// Example: '/client/projects' => '/independent/projects'
const getTargetRoute = (
  currentLocation: string,
  userType: OnboardingType | undefined,
): null | string => {
  // Exit conditions
  if (!isIndyClientSplitRoute(currentLocation)) return null;
  if (!userType) return null;

  // Flips indy vs client
  switch (userType) {
    case 'CLIENT':
      if (currentLocation === '/client/invoices') {
        // there is no independent invoices page, so we use the wallet page instead
        return '/independent/wallet';
      }

      return `${currentLocation.replace('independent', 'client')}`;
    case 'CONTRACTOR':
      if (currentLocation === '/independent/invoices/new') {
        return '/client/invoices';
      }

      return `${currentLocation.replace('client', 'independent')}`;
    default:
      return null;
  }
};

export const useUserTypeSelector = (): useUserTypeSelector => {
  const toast = useToast();
  const { pathname } = useLocation();

  const navigation = useNavigation();
  const { shouldUserOnboard } = useShouldUserOnboard();

  const {
    getReadableTargetUserType,
    isClient,
    isContractor,
    routeHelpers,
    selectedUserType,
  } = useSelectedUserType();

  const { readableTargetUserType } =
    getReadableTargetUserType(selectedUserType);

  const targetUserType =
    selectedUserType === 'CONTRACTOR' ? 'CLIENT' : 'CONTRACTOR';

  const [selectUserType] =
    useContraMutation<useUserTypeSelectorSelectUserTypeMutation>(graphql`
      mutation useUserTypeSelectorSelectUserTypeMutation(
        $input: SelectUserTypeInput!
        $type: OnboardingType!
        $owners: [ID!] = []
      ) {
        selectUserType(input: $input) {
          userAccount {
            id
            selectedUserType
            workspaceForSelectedUserType {
              __typename
              enabledFeatures
              ... on ClientWorkspace {
                activeSubscription {
                  nid
                }
                subscriptions {
                  nid
                }
              }
            }
            ...NotificationList_fragment @arguments(type: $type)
            ...UserInfoFragment_userAccount
            ...WorkspaceActivePlanCard_workspacePlanSubscription
            ...getRollupCardConfig_UserAccount @arguments(owners: $owners)
          }
          errors {
            message
            __typename
          }
          query {
            newInAppNotificationCount
          }
        }
      }
    `);

  const handleSelectUserType = useCallback<
    useUserTypeSelector['toggleSelectedUserType']
  >(
    ({
      disableRoutingChecks = false,
      skipToast = false,
    }: {
      disableRoutingChecks?: boolean;
      skipToast?: boolean;
    }) => {
      const targetRoute = getTargetRoute(pathname, targetUserType);

      selectUserType({
        onCompleted: (errors, response) => {
          const { apiErrors, payloadErrors } = errors;

          if (payloadErrors) {
            for (const payloadError of payloadErrors) {
              const errorContext = {
                locations: payloadError.locations,
                severity: payloadError.severity,
              };
              captureException(payloadError.message, {
                extra: errorContext,
              });
              log.error(errorContext, payloadError.message);
              toast.error(
                'An unexpected error occurred. Our team has been notified.',
              );
            }

            return;
          }

          if (apiErrors) {
            for (const apiError of apiErrors) {
              toast.error(apiError.message);
              log.debug(apiError, apiError.message);
            }

            return;
          }

          // If we need to onboard their user type, prompt user to complete sign up. On CTA click we will call handleSelectUserType, and redirect to onboarding
          const newlySelectedUserType =
            response.selectUserType.userAccount.selectedUserType === 'CLIENT'
              ? 'client'
              : 'independent';

          if (shouldUserOnboard(newlySelectedUserType)) {
            if (newlySelectedUserType === 'client') {
              navigation.push(
                getRoute('/get-started', { search: { userType: 'client' } }),
              );
            } else {
              navigation.push(
                getRoute('/get-started', {
                  search: { userType: 'independent' },
                }),
              );
            }
          }
          // navigate to the target route or show toast
          else if (
            targetRoute &&
            targetRoute !== pathname &&
            !disableRoutingChecks
          ) {
            navigation.push(targetRoute);
            if (skipToast) return;

            toast.success(`Successfully switched to ${readableTargetUserType}`);
          } else {
            if (skipToast) return;

            // Show toast if no navigation happens
            toast.success(`Successfully switched to ${readableTargetUserType}`);
          }
        },
        onError: (error) => {
          captureException(error);
          toast.error(
            `An error occurred switching to ${readableTargetUserType}. Please try again.`,
          );
        },
        variables: {
          input: {
            userType: targetUserType,
          },
          owners: [],
          type: targetUserType,
        },
      });
    },
    [
      navigation,
      pathname,
      readableTargetUserType,
      selectUserType,
      shouldUserOnboard,
      targetUserType,
      toast,
    ],
  );

  const { openModal: openClientModal } = useModal({
    identifier: 'ClientOnboardingRedirectionModal',
    modalProps: {
      completeSignUpPath: null,
      onCompleteSignUpClick: () => handleSelectUserType({}),
    },
  });
  const { openModal: openIndyModal } = useModal({
    identifier: 'IndyOnboardingRedirectionModal',
    modalProps: {
      completeSignUpPath: null,
      onCompleteSignUpClick: () => handleSelectUserType({}),
    },
  });

  const toggleSelectedUserType = useCallback<
    useUserTypeSelector['toggleSelectedUserType']
  >(
    ({
      disableRoutingChecks = false,
      skipToast,
    }: {
      disableRoutingChecks?: boolean;
      skipToast?: boolean;
    }) => {
      const userType = targetUserType === 'CLIENT' ? 'client' : 'independent';
      // Prompt user to complete sign up if needed,
      // On CTA click we will call handleSelectUserType, and redirect to onboarding afterwards
      if (shouldUserOnboard(userType)) {
        if (userType === 'client') {
          openClientModal();
        } else {
          openIndyModal();
        }
      } else {
        handleSelectUserType({ disableRoutingChecks, skipToast });
      }
    },
    [
      handleSelectUserType,
      openClientModal,
      openIndyModal,
      shouldUserOnboard,
      targetUserType,
    ],
  );

  return {
    getReadableTargetUserType,
    isClient,
    isContractor,
    routeHelpers,
    selectedUserType,
    toggleSelectedUserType,
  };
};
